/**
 * Recetas y su costeo con los precios reales del inventario (Req. 9, 10).
 *
 * El costeo NO guarda numeros: los calcula al momento con el costo promedio
 * vigente de cada insumo. Asi, cuando cambia un precio de compra, el costo de la
 * receta se actualiza solo.
 *
 * El congelado de costos ocurre en otro lugar y en otro momento: al completar
 * una produccion (Fase 4). Eso es lo que hace que el historial de ganancias sea
 * estable aunque los precios suban.
 */

import { db } from '../schema';
import { enEscritura } from '../tx';
import { nuevoId } from '../ids';
import {
  exigirTexto,
  exigirCantidadPositiva,
  exigirImporteNoNegativo,
  exigirEntero,
  textoOpcional,
} from '../validation';
import { ErrorValidacion } from '@/domain/errors';
import { ahoraIso } from '@/domain/dates';
import { aBase, formatearCantidadBase, formatearEnUnidad } from '@/domain/units';
import { costoPorBase } from '@/domain/inventory';
import { costearReceta, confianzaDe, type ResumenCosto, type CostoDeInsumo } from '@/domain/costing';
import type { Centavos } from '@/domain/money';
import type { Ingredient, Recipe, RecipeItem } from '../types';
import { contextoDe, estadoDe } from './ingredients';

// ---------------------------------------------------------------------------
// Alta y edicion de recetas
// ---------------------------------------------------------------------------

export interface DatosNuevaReceta {
  nombre: string;
  rendimientoPorciones: number;
  precioVentaCentavos?: Centavos;
  instrucciones?: string | null;
  diasAnticipacion?: number;
}

export async function crearReceta(datos: DatosNuevaReceta): Promise<Recipe> {
  const nombre = exigirTexto(datos.nombre, 'nombre', { maximo: 80 });
  const rendimiento = exigirCantidadPositiva(datos.rendimientoPorciones, 'rendimiento');
  const precio = exigirImporteNoNegativo(datos.precioVentaCentavos ?? 0, 'precio de venta');
  const dias = exigirEntero(datos.diasAnticipacion ?? 1, 'dias de anticipacion', {
    minimo: 0,
    maximo: 30,
  });

  const existente = (await db.recipes.toArray()).find(
    (r) => normalizar(r.nombre) === normalizar(nombre),
  );
  if (existente !== undefined) {
    throw new ErrorValidacion(`Ya existe una receta llamada "${nombre}".`, 'nombre');
  }

  const receta: Recipe = {
    id: nuevoId('rec'),
    nombre,
    categoria: null,
    rendimientoPorciones: rendimiento,
    precioVentaCentavos: precio,
    instrucciones: textoOpcional(datos.instrucciones, 'instrucciones', 4000),
    diasAnticipacion: dias,
    activo: true,
    creadoEn: ahoraIso(),
  };

  await db.recipes.add(receta);
  return receta;
}

export interface CambiosReceta {
  nombre?: string;
  rendimientoPorciones?: number;
  precioVentaCentavos?: Centavos;
  instrucciones?: string | null;
  diasAnticipacion?: number;
}

export async function editarReceta(id: string, cambios: CambiosReceta): Promise<void> {
  const receta = await db.recipes.get(id);
  if (receta === undefined) {
    throw new ErrorValidacion(`No se encontro la receta "${id}".`);
  }

  const parche: Partial<Recipe> = {};

  if (cambios.nombre !== undefined) {
    const nombre = exigirTexto(cambios.nombre, 'nombre', { maximo: 80 });
    const otra = (await db.recipes.toArray()).find(
      (r) => r.id !== id && normalizar(r.nombre) === normalizar(nombre),
    );
    if (otra !== undefined) {
      throw new ErrorValidacion(`Ya existe una receta llamada "${nombre}".`, 'nombre');
    }
    parche.nombre = nombre;
  }

  if (cambios.rendimientoPorciones !== undefined) {
    parche.rendimientoPorciones = exigirCantidadPositiva(
      cambios.rendimientoPorciones,
      'rendimiento',
    );
  }
  if (cambios.precioVentaCentavos !== undefined) {
    parche.precioVentaCentavos = exigirImporteNoNegativo(
      cambios.precioVentaCentavos,
      'precio de venta',
    );
  }
  if (cambios.instrucciones !== undefined) {
    parche.instrucciones = textoOpcional(cambios.instrucciones, 'instrucciones', 4000);
  }
  if (cambios.diasAnticipacion !== undefined) {
    parche.diasAnticipacion = exigirEntero(cambios.diasAnticipacion, 'dias de anticipacion', {
      minimo: 0,
      maximo: 30,
    });
  }

  await db.recipes.update(id, parche);
}

/** Las recetas referenciadas no se borran, se desactivan (Req. 9.10). */
export async function desactivarReceta(id: string): Promise<void> {
  await db.recipes.update(id, { activo: false });
}

export async function reactivarReceta(id: string): Promise<void> {
  await db.recipes.update(id, { activo: true });
}

/** Borra la receta y sus ingredientes. Solo si nunca se uso. */
export async function borrarReceta(id: string): Promise<void> {
  const enMenu = await db.plannedItems.where('recipeId').equals(id).count();
  const enVentas = await db.sales.where('recipeId').equals(id).count();

  if (enMenu > 0 || enVentas > 0) {
    throw new ErrorValidacion(
      'Esta receta ya se uso en el menu o en ventas, asi que no se puede borrar. ' +
        'Puedes desactivarla para que deje de aparecer.',
    );
  }

  await enEscritura([db.recipes, db.recipeItems], async () => {
    await db.recipeItems.where('recipeId').equals(id).delete();
    await db.recipes.delete(id);
  });
}

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

// ---------------------------------------------------------------------------
// Ingredientes de la receta
// ---------------------------------------------------------------------------

export interface DatosIngredienteReceta {
  recipeId: string;
  ingredientId: string;
  cantidadCaptura: number;
  unidadCaptura: string;
  notas?: string | null;
}

export async function agregarIngrediente(datos: DatosIngredienteReceta): Promise<RecipeItem> {
  const cantidad = exigirCantidadPositiva(datos.cantidadCaptura, 'cantidad');

  const receta = await db.recipes.get(datos.recipeId);
  if (receta === undefined) {
    throw new ErrorValidacion('No se encontro la receta.');
  }

  const insumo = await db.ingredients.get(datos.ingredientId);
  if (insumo === undefined) {
    throw new ErrorValidacion('No se encontro el insumo.');
  }

  const yaEsta = await db.recipeItems
    .where('recipeId')
    .equals(datos.recipeId)
    .filter((i) => i.ingredientId === datos.ingredientId)
    .first();
  if (yaEsta !== undefined) {
    throw new ErrorValidacion(
      `"${insumo.nombre}" ya esta en la receta. Edita su cantidad en lugar de agregarlo otra vez.`,
    );
  }

  // Lanza error de conversion si la unidad no corresponde al insumo (Req. 5.7)
  const cantidadBase = aBase(cantidad, datos.unidadCaptura, contextoDe(insumo));

  const item: RecipeItem = {
    id: nuevoId('ri'),
    recipeId: datos.recipeId,
    ingredientId: datos.ingredientId,
    cantidadCaptura: cantidad,
    unidadCaptura: datos.unidadCaptura,
    cantidadBase,
    escala: true,
    notas: textoOpcional(datos.notas, 'notas', 200),
  };

  await db.recipeItems.add(item);
  return item;
}

export async function editarIngrediente(
  itemId: string,
  cambios: { cantidadCaptura?: number; unidadCaptura?: string; notas?: string | null },
): Promise<void> {
  const item = await db.recipeItems.get(itemId);
  if (item === undefined) {
    throw new ErrorValidacion('No se encontro el ingrediente de la receta.');
  }

  const insumo = await db.ingredients.get(item.ingredientId);
  if (insumo === undefined) {
    throw new ErrorValidacion('No se encontro el insumo.');
  }

  const cantidad = exigirCantidadPositiva(
    cambios.cantidadCaptura ?? item.cantidadCaptura,
    'cantidad',
  );
  const unidad = cambios.unidadCaptura ?? item.unidadCaptura;

  await db.recipeItems.update(itemId, {
    cantidadCaptura: cantidad,
    unidadCaptura: unidad,
    cantidadBase: aBase(cantidad, unidad, contextoDe(insumo)),
    ...(cambios.notas !== undefined
      ? { notas: textoOpcional(cambios.notas, 'notas', 200) }
      : {}),
  });
}

export async function quitarIngrediente(itemId: string): Promise<void> {
  await db.recipeItems.delete(itemId);
}

// ---------------------------------------------------------------------------
// Consultas
// ---------------------------------------------------------------------------

export async function listarRecetas(soloActivas = true): Promise<Recipe[]> {
  const todas = await db.recipes.toArray();
  return (soloActivas ? todas.filter((r) => r.activo) : todas).sort((a, b) =>
    a.nombre.localeCompare(b.nombre, 'es'),
  );
}

export async function obtenerReceta(id: string): Promise<Recipe | undefined> {
  return db.recipes.get(id);
}

export async function ingredientesDe(recipeId: string): Promise<RecipeItem[]> {
  return db.recipeItems.where('recipeId').equals(recipeId).toArray();
}

// ---------------------------------------------------------------------------
// Costeo con los precios reales
// ---------------------------------------------------------------------------

/** Renglon de costo listo para mostrar. */
export interface RenglonCostoLegible {
  itemId: string;
  ingredientId: string;
  nombreInsumo: string;
  /** Cantidad como se capturo, por ejemplo "1 kg". */
  cantidadCaptura: string;
  /** Cantidad escalada a las porciones pedidas, en unidad legible. */
  cantidadEscalada: string;
  cantidadEscaladaBase: number;
  costoCentavos: Centavos | null;
  /** Costo del insumo por su unidad de referencia, para poder revisarlo. */
  costoUnitarioTexto: string;
  sinCosto: boolean;
  estimado: boolean;
  notas: string | null;
}

export interface CostoRecetaLegible {
  receta: Recipe;
  porciones: number;
  renglones: RenglonCostoLegible[];
  costoTotalCentavos: Centavos;
  costoPorPorcionCentavos: Centavos;
  precioVentaCentavos: Centavos;
  gananciaPorPorcionCentavos: Centavos | null;
  margenPorcentaje: number | null;
  /** 'firme' | 'estimado' | 'incompleto' */
  confianza: 'firme' | 'estimado' | 'incompleto';
  /** Nombres de los insumos sin costo, para decirlo con claridad. */
  nombresSinCosto: string[];
}

/**
 * Costea una receta guardada usando los costos vigentes del inventario.
 *
 * `porciones` permite simular: "si necesito 8 en lugar de 10, cuanto cuesta".
 * Cuando se omite, se usa el rendimiento de la receta.
 */
export async function costearRecetaGuardada(
  recipeId: string,
  porciones?: number,
): Promise<CostoRecetaLegible | undefined> {
  const receta = await db.recipes.get(recipeId);
  if (receta === undefined) return undefined;

  const items = await ingredientesDe(recipeId);
  const insumos = new Map(
    (await db.ingredients.bulkGet(items.map((i) => i.ingredientId)))
      .filter((i): i is Ingredient => i !== undefined)
      .map((i) => [i.id, i]),
  );

  const costos = new Map<string, CostoDeInsumo>();
  for (const [id, insumo] of insumos) {
    costos.set(id, costoPorBase(estadoDe(insumo)));
  }

  const resumen = costearReceta({
    items: items.map((i) => ({
      ingredientId: i.ingredientId,
      cantidadBase: i.cantidadBase,
      escala: i.escala,
      itemId: i.id,
    })),
    porciones: porciones ?? receta.rendimientoPorciones,
    rendimientoPorciones: receta.rendimientoPorciones,
    costos,
    precioVentaCentavos: receta.precioVentaCentavos,
  });

  return traducir(receta, items, insumos, resumen);
}

type ItemCosteo = { ingredientId: string; cantidadBase: number; escala: boolean; itemId: string };

function traducir(
  receta: Recipe,
  items: readonly RecipeItem[],
  insumos: ReadonlyMap<string, Ingredient>,
  resumen: ResumenCosto<ItemCosteo>,
): CostoRecetaLegible {
  const porId = new Map(items.map((i) => [i.id, i]));

  const renglones: RenglonCostoLegible[] = resumen.renglones.map((r) => {
    const item = porId.get(r.item.itemId);
    const insumo = insumos.get(r.item.ingredientId);
    const nombre = insumo?.nombre ?? '(insumo eliminado)';

    return {
      itemId: r.item.itemId,
      ingredientId: r.item.ingredientId,
      nombreInsumo: nombre,
      cantidadCaptura:
        item === undefined || insumo === undefined
          ? '—'
          : formatearEnUnidad(item.cantidadBase, item.unidadCaptura, contextoDe(insumo)),
      cantidadEscalada:
        insumo === undefined
          ? String(r.cantidadBase)
          : formatearCantidadBase(r.cantidadBase, insumo.dimension, insumo.unidadCompraPreferida),
      cantidadEscaladaBase: r.cantidadBase,
      costoCentavos: r.costoCentavos,
      costoUnitarioTexto: describirCostoUnitario(insumo, r.costoPorBase),
      sinCosto: r.costoCentavos === null,
      estimado: r.origen === 'ultima_compra',
      notas: item?.notas ?? null,
    };
  });

  const nombresSinCosto = renglones.filter((r) => r.sinCosto).map((r) => r.nombreInsumo);

  return {
    receta,
    porciones: resumen.porciones,
    renglones,
    costoTotalCentavos: resumen.costoTotalCentavos,
    costoPorPorcionCentavos: resumen.costoPorPorcionCentavos,
    precioVentaCentavos: receta.precioVentaCentavos,
    gananciaPorPorcionCentavos: resumen.gananciaPorPorcionCentavos,
    margenPorcentaje: resumen.margenPorcentaje,
    confianza: confianzaDe(resumen),
    nombresSinCosto,
  };
}

/** Texto del costo unitario del insumo, en la unidad en que se lee mejor. */
function describirCostoUnitario(
  insumo: Ingredient | undefined,
  costoPorUnidadBase: number | null,
): string {
  if (insumo === undefined || costoPorUnidadBase === null) return 'sin costo';

  const factor = insumo.dimension === 'conteo' ? 1 : 1000;
  const unidad =
    insumo.dimension === 'masa' ? 'kg' : insumo.dimension === 'volumen' ? 'l' : insumo.unidadCompraPreferida;
  const centavos = Math.round(costoPorUnidadBase * factor);

  return `${(centavos / 100).toFixed(2)} / ${unidad}`;
}

/** Costo por porcion de varias recetas, para la lista. */
export async function costoPorPorcionDeTodas(): Promise<
  Map<string, { centavos: Centavos; confianza: 'firme' | 'estimado' | 'incompleto' }>
> {
  const recetas = await listarRecetas();
  const resultado = new Map<
    string,
    { centavos: Centavos; confianza: 'firme' | 'estimado' | 'incompleto' }
  >();

  for (const receta of recetas) {
    const costo = await costearRecetaGuardada(receta.id);
    if (costo === undefined) continue;
    resultado.set(receta.id, {
      centavos: costo.costoPorPorcionCentavos,
      confianza: costo.confianza,
    });
  }

  return resultado;
}
